// AIDEV-NOTE: "Sign out of this screen" (TECH-867 B2b, L19) uses today's sign-out, which ends at Logto and comes
// back to the app's origin (`getPostLogoutRedirectUri`, the one registered URI). This marker brings the
// browser back to the same screen link, now display only. sessionStorage: per tab, survives the Logto round
// trip, and is not swept by `clearNonLogtoStorage` (its keys are listed by name).

const KEY = 'meetball_live_screen_after_sign_out';
/** Long enough for the Logto round trip; a stale marker never hijacks a later visit to "/". */
const TTL_MS = 5 * 60_000;
const SCREEN_PATH = /^\/live\/[a-z0-9]+\/screen\?key=[^&#]+$/i;

/** Before signing out from the screen: remember its path and query (the screen link). */
export const rememberScreenForSignOut = (pathAndSearch: string): void => {
  if (!SCREEN_PATH.test(pathAndSearch)) return;
  try {
    sessionStorage.setItem(KEY, JSON.stringify({ path: pathAndSearch, at: Date.now() }));
  } catch {
    // Storage blocked: the sign-out still works and lands on the app's home.
  }
};

/** A cancelled sign-out (the draft dialog) forgets it. */
export const forgetScreenForSignOut = (): void => {
  try {
    sessionStorage.removeItem(KEY);
  } catch {
    // nothing stored
  }
};

/** Read once and removed: the screen link to go back to, if the sign-out just started from one. */
export const takeScreenAfterSignOut = (now = Date.now()): string | null => {
  let raw: string | null = null;
  try {
    raw = sessionStorage.getItem(KEY);
    if (raw !== null) sessionStorage.removeItem(KEY);
  } catch {
    return null;
  }
  if (raw === null) return null;
  try {
    const value = JSON.parse(raw) as { path?: unknown; at?: unknown };
    if (typeof value.path !== 'string' || typeof value.at !== 'number') return null;
    if (now - value.at > TTL_MS || now < value.at) return null;
    return SCREEN_PATH.test(value.path) ? value.path : null;
  } catch {
    return null;
  }
};

/**
 * On the landing after Logto's sign-out (the origin's "/"), put the screen link back in the address bar before
 * the router reads it. Imported for its side effect before `./router` in `src/index.tsx`.
 */
export const resumeScreenAfterSignOut = (): void => {
  if (typeof window === 'undefined' || window.location.pathname !== '/') return;
  const path = takeScreenAfterSignOut();
  if (path !== null) window.history.replaceState(window.history.state, '', path);
};
